import { useEffect, useState } from "react";
import { Check, Crown, Loader2, CreditCard, QrCode, Barcode, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader, PlanStatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtDate, limitText, MODULE_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { MonthlyBreakdown } from "@/components/plans/MonthlyBreakdown";
import { ChargeDialog } from "@/components/plans/ChargeDialog";
import { CustomPlanBuilder } from "@/components/plans/CustomPlanBuilder";
import { CompleteRegistrationDialog } from "@/components/plans/CompleteRegistrationDialog";
import { readChoice, saveChoice, startingPrice, useModuleCatalog } from "@/lib/modules";

const USAGE_LABELS = { users: "Usuários ativos", products: "Produtos", orders: "Pedidos neste mês", stores: "Unidades" };

function StatusCard({ sub, planName }) {
  return (
    <div className="card-surface p-5 flex flex-wrap items-center gap-4" data-testid="plans-status-card">
      <PlanStatusBadge status={sub.status} />
      <p className="text-sm flex-1">
        {sub.status === "trial" && `Seu teste grátis termina em ${fmtDate(sub.trial_end)} (${sub.days_left} dias restantes). Durante o teste todos os recursos estão liberados.`}
        {sub.status === "expired" && (sub.paid_until ? `Sua assinatura venceu em ${fmtDate(sub.paid_until)}. Renove para voltar a vender.` : `Seu teste terminou em ${fmtDate(sub.trial_end)}. Seus dados estão salvos — assine para voltar a vender.`)}
        {sub.status === "active" && `Plano ${planName || sub.plan} ativo${sub.paid_until ? ` até ${fmtDate(sub.paid_until)}` : ""}. Você pode renovar antecipadamente — os dias são somados.`}
        {["suspended", "cancelled"].includes(sub.status) && sub.alert}
      </p>
    </div>
  );
}

function UsageCard({ usage }) {
  if (!usage?.plan) return null;
  return (
    <div className="card-surface p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="plan-usage-card">
      {Object.keys(USAGE_LABELS).map((k) => {
        const lim = usage.limits[k], used = usage.usage[k];
        return (
          <div key={k} className="space-y-2" data-testid={`usage-${k}`}>
            <div className="flex justify-between text-sm"><span>{USAGE_LABELS[k]}</span><span className="font-mono">{used}{lim ? ` / ${lim}` : " · ilimitado"}</span></div>
            <Progress value={lim ? Math.min(100, (used / lim) * 100) : 4} className={cn("h-2", lim && used >= lim && "[&>div]:bg-red-500")} />
          </div>
        );
      })}
    </div>
  );
}

const planFeatures = (p) => [
  limitText(p.max_users, "usuário", "usuários"), limitText(p.max_products, "produto", "produtos"),
  limitText(p.max_orders, "pedido por mês", "pedidos por mês"), limitText(p.max_stores, "loja", "lojas"),
  p.support && `Suporte: ${p.support}`, ...(p.features || []),
].filter(Boolean);

export default function Plans() {
  const { session } = useAuth();
  const sub = session.subscription;
  const [plans, setPlans] = useState([]);
  const [opt, setOpt] = useState(null);
  const [usage, setUsage] = useState(null);
  const [selected, setSelected] = useState(sub.plan || "profissional");
  const [period, setPeriod] = useState("mensal");
  const [busy, setBusy] = useState(null);
  const [charge, setCharge] = useState(null);
  const catalog = useModuleCatalog();
  const [mods, setMods] = useState(null);
  const [quote, setQuote] = useState(null);
  const [completar, setCompletar] = useState(null); // ação de pagamento que espera o cadastro completo (1ª contratação)
  useEffect(() => {
    const escritorio = session.tenant?.segmento === "contabilidade";
    api.get("/plans").then((r) => {
      const lista = r.data.filter((p) => (p.audience === "contabilidade") === escritorio);
      setPlans(lista);
      if (escritorio && !sub.plan && lista[0]) setSelected(lista[0].id);
    });
    api.get("/billing/options").then((r) => setOpt(r.data));
    api.get("/plan/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (opt && mods === null) setMods(opt.custom_choice?.length ? opt.custom_choice : readChoice());
  }, [opt, mods]);
  const selPlan = plans.find((p) => p.id === selected);
  const customSel = !!selPlan?.custom;
  const chosen = customSel ? (mods || []).filter((m) => selPlan.modules.includes(m)) : [];
  const chosenKey = chosen.join(",");
  useEffect(() => {
    if (!customSel || !chosenKey) { setQuote(null); return; }
    api.post("/billing/quote", { plan_id: selected, modules: chosenKey.split(",") }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [customSel, selected, chosenKey]);
  const pickMods = (v) => { setMods(v); saveChoice(v); };

  // Primeira contratação: sem cadastro completo, abre "Complete o cadastro" e depois segue para o pagamento escolhido.
  const precisaCompletar = (acao) => {
    if (!opt?.cadastro?.exigido) return false;
    setCompletar(() => acao);
    return true;
  };
  const incompleto = (e, acao) => {
    if (e?.response?.status !== 409) return false;
    setOpt((o) => ({ ...o, cadastro: { ...(o?.cadastro || {}), exigido: true } }));
    setCompletar(() => acao);
    return true;
  };
  const aposCompletar = async () => {
    const acao = completar;
    setCompletar(null);
    const { data } = await api.get("/billing/options").catch(() => ({ data: null }));
    if (data) setOpt(data);
    if (acao && data && !data.cadastro?.exigido) acao();
  };
  const pay = async (provider, depoisDeCompletar = false) => {
    if (!depoisDeCompletar && precisaCompletar(() => pay(provider, true))) return;
    setBusy(provider);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: selected, period, provider, origin_url: window.location.origin, modules: chosen });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (!incompleto(e, () => pay(provider, true))) toast.error(errMsg(e));
      setBusy(null);
    }
  };
  const genCharge = async (method, depoisDeCompletar = false) => {
    if (!depoisDeCompletar && precisaCompletar(() => genCharge(method, true))) return;
    setBusy(method);
    try {
      const { data } = await api.post("/billing/charge", { plan_id: selected, period, method, modules: chosen });
      setCharge(data);
      setOpt((o) => ({ ...o, pending_charge: data }));
    } catch (e) {
      if (!incompleto(e, () => genCharge(method, true))) toast.error(errMsg(e));
    }
    setBusy(null);
  };
  const btnIcon = (k, Icon) => (busy === k ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />);
  const price = (id) => (plans.find((p) => p.id === id)?.custom ? (id === selected ? quote?.prices[period] : undefined) : opt?.prices[id]?.[period]);
  const per = opt?.periods.find((p) => p.id === period);
  const fromPrice = (p) => p.custom && !(selected === p.id && quote);

  return (
    <>
      <PageHeader eyebrow="Assinatura" title="Planos" subtitle="Escolha o plano e o período. O acesso é liberado automaticamente após o pagamento." />
      <StatusCard sub={sub} planName={usage?.plan?.name} />
      <UsageCard usage={usage} />
      {opt?.pending_charge && (
        <div className="card-surface p-4 flex flex-wrap items-center gap-3 border-amber-500/40" data-testid="pending-charge-banner">
          <Clock className="h-5 w-5 text-amber-500" />
          <p className="text-sm flex-1">Você tem {opt.pending_charge.method === "pix" ? "um Pix" : "um boleto"} de <b>{brl(opt.pending_charge.amount)}</b> aguardando pagamento.</p>
          <Button size="sm" variant="outline" onClick={() => setCharge(opt.pending_charge)} data-testid="pending-charge-view-btn">Ver cobrança</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid="period-selector">
        {opt?.periods.map((p) => (
          <Button key={p.id} variant={period === p.id ? "default" : "outline"} onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}>
            {p.label}{(p.id === "anual" ? opt.yearly_discounts?.[selected] ?? p.discount_pct : p.discount_pct) > 0 && <span className="ml-2 rounded-full bg-emerald-500/20 px-2 text-xs text-emerald-500" data-testid={`period-discount-${p.id}`}>-{p.id === "anual" ? opt.yearly_discounts?.[selected] ?? p.discount_pct : p.discount_pct}%</span>}
          </Button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={cn("card-surface p-6 flex flex-col gap-4 relative", selected === p.id && "border-primary shadow-[0_0_20px_hsl(var(--primary)/0.25)]")} data-testid={`plan-card-${p.id}`}>
            {p.highlight && <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">Mais escolhido</span>}
            <div><div className="flex items-center gap-2"><Crown className="h-5 w-5 text-primary" /><h3 className="font-display text-xl font-bold">{p.name}</h3></div>
              {p.description && <p className="text-sm text-muted-foreground mt-1">{p.description}</p>}</div>
            <p>{fromPrice(p) && <span className="text-sm text-muted-foreground">a partir de </span>}<span className="font-display text-3xl font-extrabold" data-testid={`plan-price-${p.id}`}>{brl(fromPrice(p) ? startingPrice(p) : price(p.id) ?? p.price)}</span><span className="text-muted-foreground"> / {fromPrice(p) ? "mês" : per ? `${per.days} dias` : "mês"}</span></p>
            {per?.months > 1 && !fromPrice(p) && <p className="-mt-3 text-xs text-muted-foreground">equivale a {brl((price(p.id) || 0) / per.months)}/mês</p>}
            {opt?.extras?.[p.id]?.length > 0 && <p className="-mt-3 text-xs text-primary" data-testid={`plan-extras-${p.id}`}>inclui {opt.extras[p.id].length} módulo(s) extra(s) da sua conta</p>}
            <ul className="space-y-2 text-sm">{planFeatures(p).map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{f}</li>)}</ul>
            {p.custom ? <p className="flex-1 text-xs text-muted-foreground" data-testid={`plan-custom-hint-${p.id}`}>Você escolhe entre {p.modules.length} módulos e paga só os que usar.</p>
              : <div className="flex flex-wrap gap-1 flex-1 content-start">{p.modules.map((m) => <span key={m} className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{MODULE_LABELS[m] || m}</span>)}</div>}
            <Button variant={selected === p.id ? "default" : "outline"} onClick={() => setSelected(p.id)} data-testid={`choose-plan-btn-${p.id}`}>{selected === p.id ? "Selecionado" : p.custom ? "Montar meu plano" : "Escolher plano"}</Button>
          </div>
        ))}
      </div>
      {customSel && <CustomPlanBuilder plan={selPlan} catalog={catalog} mods={chosen} setMods={pickMods} />}
      <MonthlyBreakdown plan={customSel ? { ...selPlan, price: quote?.price || 0 } : selPlan} extras={(customSel ? quote?.extras : opt?.extras?.[selected]) || []} />
      <div className="card-surface p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-sm flex-1">Total: <b className="font-mono text-lg" data-testid="checkout-total">{brl(price(selected) ?? 0)}</b> · {per?.label} · liberação automática</p>
        <Button size="lg" className="h-12" onClick={() => pay("stripe")} disabled={!!busy || (customSel && !chosen.length)} data-testid="pay-stripe-btn">{btnIcon("stripe", CreditCard)}Pagar com cartão</Button>
        {opt?.providers.includes("pix") && <Button size="lg" variant="outline" className="h-12" onClick={() => genCharge("pix")} disabled={!!busy || (customSel && !chosen.length)} data-testid="pay-pix-btn">{btnIcon("pix", QrCode)}Pagar com Pix</Button>}
        {opt?.providers.includes("boleto") && <Button size="lg" variant="outline" className="h-12" onClick={() => genCharge("boleto")} disabled={!!busy || (customSel && !chosen.length)} data-testid="pay-boleto-btn">{btnIcon("boleto", Barcode)}Gerar boleto</Button>}
      </div>
      {opt?.cadastro?.exigido && <p className="text-xs text-muted-foreground" data-testid="plans-registration-hint">Antes do primeiro pagamento vamos pedir o endereço da empresa (para a cobrança e a nota fiscal).</p>}
      <ChargeDialog charge={charge} onClose={() => setCharge(null)} />
      <CompleteRegistrationDialog open={!!completar} onOpenChange={(v) => !v && setCompletar(null)} onDone={aposCompletar} />
    </>
  );
}
